import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { useAuth } from './AuthContext';

const SystemContext = createContext();

export const useSystem = () => {
  const context = useContext(SystemContext);
  if (!context) {
    throw new Error('useSystem must be used within a SystemProvider');
  }
  return context;
};

export const SystemProvider = ({ children }) => {
  const [systemInfo, setSystemInfo] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      loadSystemData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [systemInfoData, tenantsData, integrationsData] = await Promise.all([
        apiService.getSystemInfo(),
        apiService.getTenants(),
        apiService.getIntegrations()
      ]);

      setSystemInfo(systemInfoData);
      setTenants(tenantsData);
      setIntegrations(integrationsData);
    } catch (error) {
      console.error('Error loading system data:', error);
      setError('Sistem verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async (tenantData) => {
    try {
      const newTenant = await apiService.createTenant(tenantData);
      setTenants(prev => [...prev, newTenant]);
      return newTenant;
    } catch (error) {
      console.error('Error creating tenant:', error);
      throw error;
    }
  };

  const updateTenant = async (id, tenantData) => {
    try {
      const updatedTenant = await apiService.updateTenant(id, tenantData);
      setTenants(prev => prev.map(tenant => 
        tenant.id === id ? updatedTenant : tenant
      ));
      return updatedTenant;
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  };

  const deleteTenant = async (id) => {
    try {
      await apiService.deleteTenant(id);
      setTenants(prev => prev.filter(tenant => tenant.id !== id));
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  };

  const value = {
    systemInfo,
    tenants,
    integrations,
    loading,
    error,
    loadSystemData,
    createTenant,
    updateTenant,
    deleteTenant,
  };

  return (
    <SystemContext.Provider value={value}>
      {children}
    </SystemContext.Provider>
  );
};
